import React from "react";
import { Responsive, ResponsiveProps, WidthProvider } from "react-grid-layout";
import { ErrorBoundary, RemoteVideoLayoutOptions } from "../../common";
import styles from "./ResponsiveGridLayout.module.css";

const Layout = WidthProvider(Responsive);

export interface GridItemRenderInfo extends RemoteVideoLayoutOptions {
    displayName: string;
    videoSource: MediaStream | string;
}

type ItemRenderer = (info: GridItemRenderInfo) => JSX.Element;

export default function ResponsiveGridLayout(
    props: ResponsiveProps & {
        currentNumCols: number;
        rowHeight: number;
        items: GridItemRenderInfo[];
        itemRenderer: ItemRenderer;
    }
) {
    // see https://github.com/STRML/react-grid-layout#performance
    const children = React.useMemo(() => props.items.map(props.itemRenderer), [
        props.items.length,
        getLayoutOrderingMemoKey(props.items),
    ]);
    return (
        <ErrorBoundary>
            <Layout
                {...props}
                className={styles.container}
                layouts={getResponsiveLayouts(props.items, props.currentNumCols)}
                rowHeight={props.rowHeight}
                draggableCancel={`.${styles.notDraggable}`}
                compactType="vertical"
                margin={[2, 2]}
            >
                {children}
            </Layout>
        </ErrorBoundary>
    );
}

function getResponsiveLayouts(videoLayout: RemoteVideoLayoutOptions[], currentNumCols: number) {
    return {
        default: videoLayout.map(({ userId, isPinned, gridWidth, gridHeight, gridX, gridY }, idx) => {
            return {
                i: userId,
                x: gridX ?? idx % currentNumCols,
                y: gridY ?? Math.floor(idx / currentNumCols),
                w: gridWidth ?? 1,
                h: gridHeight ?? 1,
                isDraggable: !isPinned,
            };
        }),
    };
}

function getLayoutOrderingMemoKey(videoLayout: RemoteVideoLayoutOptions[]) {
    return videoLayout.map((l) => l.userId).join(",");
}
